import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { PageTemplate, HeaderTemplate, Button } from "../styles";
import { createMember, getMemberSignupProof, resendMemberVerification } from "../api/member";

const MemberSignup = () => {
  const [emailAddress, setEmailAddress] = useState("");
  const [registered, setRegistered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formProof, setFormProof] = useState("");
  const [proofReadyAt, setProofReadyAt] = useState(0);
  const [website, setWebsite] = useState("");
  const [resendWait, setResendWait] = useState(false);

  useEffect(() => {
    let active = true;
    getMemberSignupProof()
      .then((proof) => {
        if (active) {
          setFormProof(proof);
          setProofReadyAt(Date.now() + 2100);
        }
      })
      .catch(() => {
        if (active) toast.error("Unable to load signup form. Please refresh.");
      });
    return () => {
      active = false;
    };
  }, []);

  const handleResend = async () => {
    if (loading || resendWait) return;
    setLoading(true);
    try {
      const proof = await getMemberSignupProof();
      // The proof must age at least 2 seconds before submission.
      await new Promise((resolve) => setTimeout(resolve, 2100));
      await resendMemberVerification(emailAddress.trim().toLowerCase(), proof, website);
      toast.success("If verification is needed, another email will arrive shortly.");
      setResendWait(true);
      window.setTimeout(() => setResendWait(false), 60000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || registered || !formProof) return;

    setLoading(true);
    try {
      if (Date.now() < proofReadyAt) await new Promise((resolve) => setTimeout(resolve, proofReadyAt - Date.now()));
      await createMember(emailAddress.trim().toLowerCase(), formProof, website);
      setRegistered(true);
      toast.success("Please check your inbox to confirm your email address.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <HeaderTemplate className="narrow">
        <div className="header">
          <div className="title">
            <h1>Member Perks</h1>
          </div>
        </div>
      </HeaderTemplate>

      <PageTemplate className="narrow">
        {registered ? (
          <div role="status">
            <p>Please check your inbox to confirm your email address. You won't be subscribed until you confirm.</p>
            <p>Didn't receive the email? Check your spam folder or request another link.</p>
            <Button type="button" onClick={handleResend} disabled={loading || resendWait}>
              {loading ? "Sending..." : resendWait ? "Please wait before resending" : "Resend Verification Email"}
            </Button>
          </div>
        ) : (
          <>
            <p>Join our email list to receive Grey Gull updates, exclusive member offers, and promotional pricing throughout the year.</p>
            <form onSubmit={handleSubmit}>
              <Form.Group controlId="member-email">
                <Form.Control
                  required
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="email address"
                  disabled={loading}
                />
              </Form.Group>
              <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
                <label htmlFor="member-website">Website</label>
                <input
                  id="member-website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={loading || !formProof} style={{ marginTop: "20px" }}>
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </form>
          </>
        )}
      </PageTemplate>
    </>
  );
};

export default MemberSignup;
